'use client';

import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import {
    faBank,
    faBook,
    faBorderAll,
    faCashRegister,
    faChalkboardTeacher,
    faFileInvoice,
    faHorseHead,
    faKey,
    faNewspaper,
    faTicketAlt,
    faUsers,
    faWindowMaximize
} from '@fortawesome/free-solid-svg-icons';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { SectionStatus } from './SettingsPanelContext';

/**
 * Single source of truth for settings-section metadata.
 *
 * Both the settings pages (which spread these props onto <SettingsSection>) and the
 * global Cmd+K registry (components/CommandPalette/settingsSectionsNavItems.ts) read
 * from here, so titles/keywords can never drift between the page and the palette.
 *
 * Text lives in the `settings` namespace (public/static/locales/{de,en}/settings.json,
 * under modules.<segment>.sections.<id>). Each module has a `build<Module>Settings(t)`
 * function taking a TFunction, plus a `use<Module>Settings()` hook wrapper for use inside
 * components (call the hook; use the plain builder with `i18n.t` outside React render,
 * e.g. in clubPages.ts, the same way other translated nav entries do).
 *
 * To add or rename a section: add the id (+ optional status) to the section-id list
 * below, and add the matching title/description/keywords under modules.<segment>.sections
 * in both locale files. The page renders it and Cmd+K picks it up automatically. The
 * section's children (form fields) still live in the page component.
 */
export interface SettingsSectionConfig {
    id: string;
    title: string;
    description?: string;
    keywords?: string;
    status?: SectionStatus;
}

export interface SettingsModuleConfig {
    /** URL segment under /club/[slug]/ — e.g. 'invoices'. */
    segment: string;
    /** Human-readable module name, shown as the Cmd+K subtitle and the Einstellungen hub tab. */
    name: string;
    /** Icon for the Einstellungen hub tab + landing card. */
    icon: IconDefinition;
    /** Permissions required to access this module's settings (used to gate the hub tab). */
    permissions?: string[];
    /** Experimental feature key the module is behind — the hub tab stays hidden without it. */
    experimental?: string;
    /** Legacy modules (e.g. SpaceBooking 'spaces') are inactive: kept for the page but hidden from nav. */
    active: boolean;
    /** Ordered list of sections as they appear on the page. */
    sections: SettingsSectionConfig[];
    /** Sections keyed by id for ergonomic spreading: {...module.byId['qr']} */
    byId: Record<string, SettingsSectionConfig>;
}

interface ModuleMeta {
    segment: string;
    icon: IconDefinition;
    permissions?: string[];
    experimental?: string;
    active?: boolean;
}

interface SectionMeta {
    id: string;
    status?: SectionStatus;
}

/** Resolves a module's section ids into translated section configs plus a byId lookup. */
function buildModule(t: TFunction<'settings'>, meta: ModuleMeta, sectionMetas: SectionMeta[]): SettingsModuleConfig {
    const sections = sectionMetas.map(({ id, status }) => ({
        id,
        title: t(`modules.${meta.segment}.sections.${id}.title`),
        description: t(`modules.${meta.segment}.sections.${id}.description`),
        keywords: t(`modules.${meta.segment}.sections.${id}.keywords`),
        status
    }));
    const byId = Object.fromEntries(sections.map((s) => [s.id, s])) as Record<string, SettingsSectionConfig>;
    return {
        segment: meta.segment,
        name: t(`modules.${meta.segment}.name`),
        icon: meta.icon,
        permissions: meta.permissions,
        experimental: meta.experimental,
        active: meta.active ?? true,
        sections,
        byId
    };
}

/** Invoice module settings sections. */
export const buildInvoiceSettings = (t: TFunction<'settings'>): SettingsModuleConfig =>
    buildModule(t, { segment: 'invoices', icon: faFileInvoice, permissions: ['user_invoice.view'] }, [
        { id: 'missing-data', status: 'needs-setup' },
        { id: 'display', status: 'configured' },
        { id: 'suggestions', status: 'optional' },
        { id: 'delivery', status: 'optional' },
        { id: 'taxes', status: 'optional' },
        { id: 'dunning', status: 'optional' },
        { id: 'dates', status: 'optional' },
        { id: 'release', status: 'optional' },
        { id: 'qr', status: 'optional' },
        { id: 'sepa', status: 'optional' }
    ]);

/** Event booking module settings sections. */
export const buildEventSettings = (t: TFunction<'settings'>): SettingsModuleConfig =>
    buildModule(t, { segment: 'bookings', icon: faTicketAlt, permissions: ['event_calendar.settings'] }, [
        { id: 'notifications-bookers' },
        { id: 'notifications-responsible' },
        { id: 'invoices' },
        { id: 'events' },
        { id: 'bookings' },
        { id: 'iframe' }
    ]);

/** Newsletter/feed module settings sections. */
export const buildFeedSettings = (t: TFunction<'settings'>): SettingsModuleConfig =>
    buildModule(t, { segment: 'feed', icon: faNewspaper, permissions: ['message.view'] }, [
        { id: 'self-registration' },
        { id: 'default-audience' }
    ]);

/** Training journal module settings sections. */
export const buildJournalSettings = (t: TFunction<'settings'>): SettingsModuleConfig =>
    buildModule(
        t,
        { segment: 'journal', icon: faBook, permissions: ['trainingsjournal.entry.view', 'trainingsjournal.template.view'] },
        [
            { id: 'notifications' },
            { id: 'app-cancellations' },
            { id: 'app-quick-actions' },
            { id: 'trainer-form' },
            { id: 'open-trainings' },
            { id: 'training-types' },
            { id: 'import' },
            { id: 'form-data-release' },
            { id: 'trainer-hours-sync' },
            { id: 'allocations-sync' },
            { id: 'table' },
            { id: 'categories' },
            { id: 'recommendations' },
            { id: 'reminders' },
            { id: 'billing-export' }
        ]
    );

/** Trainer booking module settings sections. */
export const buildTrainerBookingSettings = (t: TFunction<'settings'>): SettingsModuleConfig =>
    buildModule(t, { segment: 'trainer-booking', icon: faChalkboardTeacher, permissions: ['trainer_booking.settings'] }, [
        { id: 'notifications' },
        { id: 'invoices' },
        { id: 'description' },
        { id: 'customer-emails' },
        { id: 'prices' },
        { id: 'view' }
    ]);

/** Voucher module settings sections. */
export const buildVoucherSettings = (t: TFunction<'settings'>): SettingsModuleConfig =>
    buildModule(t, { segment: 'vouchers', icon: faWindowMaximize, permissions: ['vouchers.voucher.view'] }, [
        { id: 'pdf' },
        { id: 'invoices' }
    ]);

/** Space manager module settings sections. */
export const buildSpaceManagerSettings = (t: TFunction<'settings'>): SettingsModuleConfig =>
    buildModule(t, { segment: 'space-manager', icon: faBorderAll }, [
        { id: 'app' },
        { id: 'refund' },
        { id: 'members' },
        { id: 'booking' },
        { id: 'iframe' },
        { id: 'kiosk' },
        { id: 'notifications' },
        { id: 'invoices' },
        { id: 'view' }
    ]);

/** Member management module settings sections. */
export const buildMemberSettings = (t: TFunction<'settings'>): SettingsModuleConfig =>
    buildModule(t, { segment: 'members', icon: faUsers, permissions: ['member_management.settings'] }, [
        { id: 'authorization' },
        { id: 'property-transfer' },
        { id: 'member-number' },
        { id: 'dynamic-categories' },
        { id: 'billing-groups' },
        { id: 'service-hours' },
        { id: 'cancellations' },
        { id: 'debtor' },
        { id: 'group-invoice-limit' },
        { id: 'card-template' },
        { id: 'letter' },
        { id: 'dashboard' },
        { id: 'action-buttons' },
        { id: 'invoices' },
        { id: 'view' },
        { id: 'applications' },
        { id: 'iframe' },
        { id: 'application-mapping' },
        { id: 'editable-properties' },
        { id: 'birthday-notification' },
        { id: 'application-accepted' },
        { id: 'cancellation-confirmation' }
    ]);

/** Banking module settings sections — the bank connection lives here, gated only by permission. */
export const buildBankingSettings = (t: TFunction<'settings'>): SettingsModuleConfig =>
    buildModule(
        t,
        { segment: 'banking', icon: faBank, permissions: ['banking.manage'] },
        // No static status — the page overrides it with the live connection state.
        [{ id: 'connection' }]
    );

/** Legacy SpaceBooking module — commented out of the nav (active: false), so it's hidden from the
 *  Einstellungen hub, the landing, and Cmd+K. Kept so the page itself (if reached directly) stays in sync. */
export const buildSpaceBookingSettings = (t: TFunction<'settings'>): SettingsModuleConfig =>
    buildModule(t, { segment: 'spaces', icon: faBorderAll, permissions: ['space_booking.settings'], active: false }, [
        { id: 'invoices' },
        { id: 'customer-emails' },
        { id: 'prices' },
        { id: 'fellows' },
        { id: 'refund' },
        { id: 'view' },
        { id: 'kiosk' }
    ]);

/**
 * Till & shop module settings sections.
 *
 * The former `shop_v1` gate is gone — the module is generally available since 14.08.2026.
 * No plan gate here on purpose: the hub knows permissions and flags, not subscriptions, and
 * the invoice module is listed the same way. A club without `invoicetool` lands on the
 * upgrade notice of /shop, which is where CheckSubscription belongs.
 */
export const buildShopSettings = (t: TFunction<'settings'>): SettingsModuleConfig =>
    buildModule(
        t,
        { segment: 'shop', icon: faCashRegister, permissions: ['shop.settings'] },
        [
            { id: 'self-booking' },
            { id: 'kiosk' },
            { id: 'payment' },
            { id: 'invoicing' },
            { id: 'auto-billing' },
            { id: 'stock-alerts' }
        ]
    );

/** Equestrian (Reitsport) module settings sections. */
export const buildEquestrianSettings = (t: TFunction<'settings'>): SettingsModuleConfig =>
    buildModule(
        t,
        {
            segment: 'horses',
            icon: faHorseHead,
            permissions: ['equestrian.settings'],
            // Same gate the module itself carries in clubPages.ts, so the hub tab cannot
            // appear for a club that does not have the module yet.
            experimental: 'equestrian_v1'
        },
        [
            { id: 'billing', status: 'optional' },
            { id: 'auto-billing', status: 'optional' }
        ]
    );

/** Access control (Zutritt) module settings sections. Gated by the same permission as the settings page itself. */
export const buildAccessSettings = (t: TFunction<'settings'>): SettingsModuleConfig =>
    buildModule(t, { segment: 'access', icon: faKey, permissions: ['access_control.settings'] }, [{ id: 'pin' }]);

/** All modules, in nav order. Consumed by the Cmd+K registry and the Einstellungen hub. */
export const buildSettingsModules = (t: TFunction<'settings'>): SettingsModuleConfig[] => [
    buildInvoiceSettings(t),
    buildEventSettings(t),
    buildFeedSettings(t),
    buildJournalSettings(t),
    buildTrainerBookingSettings(t),
    buildVoucherSettings(t),
    buildSpaceManagerSettings(t),
    buildMemberSettings(t),
    buildBankingSettings(t),
    buildShopSettings(t),
    buildEquestrianSettings(t),
    buildAccessSettings(t),
    buildSpaceBookingSettings(t)
];

/** Shorthand for the settings-namespace translator used by the hook wrappers below. */
function useSettingsT() {
    return useTranslation('settings').t;
}

/** Hook wrappers around the builders above - one per module, for use inside components. */
export const useInvoiceSettings = (): SettingsModuleConfig => buildInvoiceSettings(useSettingsT());
export const useEventSettings = (): SettingsModuleConfig => buildEventSettings(useSettingsT());
export const useFeedSettings = (): SettingsModuleConfig => buildFeedSettings(useSettingsT());
export const useJournalSettings = (): SettingsModuleConfig => buildJournalSettings(useSettingsT());
export const useTrainerBookingSettings = (): SettingsModuleConfig => buildTrainerBookingSettings(useSettingsT());
export const useVoucherSettings = (): SettingsModuleConfig => buildVoucherSettings(useSettingsT());
export const useSpaceManagerSettings = (): SettingsModuleConfig => buildSpaceManagerSettings(useSettingsT());
export const useMemberSettings = (): SettingsModuleConfig => buildMemberSettings(useSettingsT());
export const useBankingSettings = (): SettingsModuleConfig => buildBankingSettings(useSettingsT());
export const useShopSettings = (): SettingsModuleConfig => buildShopSettings(useSettingsT());
export const useAccessSettings = (): SettingsModuleConfig => buildAccessSettings(useSettingsT());
export const useEquestrianSettings = (): SettingsModuleConfig => buildEquestrianSettings(useSettingsT());
export const useSpaceBookingSettings = (): SettingsModuleConfig => buildSpaceBookingSettings(useSettingsT());
export const useSettingsModules = (): SettingsModuleConfig[] => buildSettingsModules(useSettingsT());
